/**
 * Config-driven detection — Prompt.md §14.
 *
 * KHÔNG hard-code threshold trong PostureEngine. Mọi số magic của hệ thống sống ở package này,
 * để sau này thêm được: config theo độ tuổi, config theo thiết bị, personalized threshold, remote config.
 *
 * Theo BLOCKER #1 (Option C), mỗi `CameraPlacement` có một bộ threshold riêng — cùng một độ cúi
 * đầu tạo ra delta góc khác nhau khi camera nhìn chính diện so với nhìn ngang.
 */

import type { CameraPlacement } from "@studystraight/shared-types";

/** Ngưỡng cho một issue đo bằng **delta góc (độ)** so với baseline. */
export interface DeltaThreshold {
  warningDelta: number;
  badDelta: number;
}

/** Ngưỡng cho một issue đo bằng **tỉ lệ** so với baseline (§13 Too Close). */
export interface RatioThreshold {
  warningRatio: number;
  badRatio: number;
}

export interface PostureConfig {
  /**
   * Dưới ngưỡng này, landmark bị coi là KHÔNG quan sát được → `Metric.available = false`.
   * Pose engine vẫn trả toạ độ suy đoán cho landmark bị che, nên đây là cổng duy nhất
   * ngăn dữ liệu rác đi vào công thức (DU #3).
   */
  minLandmarkVisibility: number;

  /** Dưới ngưỡng này, cả frame bị bỏ (§16 confidence filtering). */
  minFrameConfidence: number;

  /**
   * `Metric.available` phải giữ ổn định bao lâu mới được đổi trạng thái.
   * Không có nó, landmark dao động quanh ngưỡng 0.5 sẽ bật/tắt metric liên tục và làm
   * posture score nhảy bậc (edge case #6).
   */
  availabilityHysteresisMs: number;

  headDown: DeltaThreshold;
  leaningForward: DeltaThreshold;
  shoulderTilt: DeltaThreshold;
  tooClose: RatioThreshold;
}

export const defaultPostureConfig: Record<CameraPlacement, PostureConfig> = {
  FRONTAL: {
    minLandmarkVisibility: 0.5,
    minFrameConfidence: 0.5,
    availabilityHysteresisMs: 1000,
    headDown: { warningDelta: 12, badDelta: 20 }, // §14
    // Ở góc chính diện đây là metric yếu nhất (DU #3): chỉ khả dụng khi thấy được hông.
    leaningForward: { warningDelta: 10, badDelta: 18 },
    shoulderTilt: { warningDelta: 8, badDelta: 14 },
    tooClose: { warningRatio: 1.2, badRatio: 1.4 }, // §14
  },
  SIDE: {
    minLandmarkVisibility: 0.5,
    minFrameConfidence: 0.5,
    availabilityHysteresisMs: 1000,
    // Góc tai↔vai ở profile view nhạy hơn với cùng một độ cúi → hạ ngưỡng.
    headDown: { warningDelta: 10, badDelta: 18 },
    // Đo trực tiếp từ vai↔hông — đây là giá trị chính mà mode SIDE mang lại.
    leaningForward: { warningDelta: 8, badDelta: 15 },
    // Giữ để config đồng dạng giữa 2 placement, nhưng KHÔNG được dùng:
    // xem ISSUE_AVAILABILITY.SIDE.SHOULDER_TILT === false trong posture-core.
    shoulderTilt: { warningDelta: 8, badDelta: 14 },
    tooClose: { warningRatio: 1.2, badRatio: 1.4 },
  },
};
